import type { EpochMs, LightPhase, SunEventName } from './types.ts';
import { angleDiffDeg, normalizeDeg } from './geo.ts';
import { localDayBounds } from './tz.ts';
import { compassPoint, formatDuration, MINUS } from './format.ts';

/*
 * Sun position: NOAA Solar Calculator algorithm (the equations of the NOAA "NOAA_Solar_Calculations_day"
 * spreadsheet, after Meeus), valid ~±1 min for sunrise/sunset at mid latitudes.
 *
 * Elevation conventions:
 * - `sunPosition().elevationDeg` is APPARENT (NOAA refraction model applied). `lightPhase` and
 *   golden/blue hour boundaries (+6°, −4°) use apparent elevation.
 * - Standard events use GEOMETRIC elevation of the sun's centre: sunrise/sunset −0.833°
 *   (refraction + semi-diameter), civil −6°, nautical −12°, astronomical −18°.
 */

export interface SunPosition {
  /** Degrees true, clockwise from north. */
  azimuthDeg: number;
  /** Degrees above horizon, including standard refraction. */
  elevationDeg: number;
}

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

interface SolarGeometry {
  /** Geometric (true) elevation, no refraction. */
  elevationDeg: number;
  azimuthDeg: number;
  /** Hour angle, degrees in [-180,180): 0 at local solar noon, negative before. */
  hourAngleDeg: number;
}

function solarGeometry(t: EpochMs, lat: number, lon: number): SolarGeometry {
  const jd = t / 86400000 + 2440587.5;
  const T = (jd - 2451545) / 36525;
  const L0 = normalizeDeg(280.46646 + T * (36000.76983 + T * 0.0003032));
  const M = 357.52911 + T * (35999.05029 - 0.0001537 * T);
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T);
  const Mr = M * D2R;
  const C =
    Math.sin(Mr) * (1.914602 - T * (0.004817 + 0.000014 * T)) +
    Math.sin(2 * Mr) * (0.019993 - 0.000101 * T) +
    Math.sin(3 * Mr) * 0.000289;
  const trueLong = L0 + C;
  const omega = (125.04 - 1934.136 * T) * D2R;
  const appLong = (trueLong - 0.00569 - 0.00478 * Math.sin(omega)) * D2R;
  const eps0 = 23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60;
  const eps = (eps0 + 0.00256 * Math.cos(omega)) * D2R;
  const decl = Math.asin(Math.sin(eps) * Math.sin(appLong));
  const y = Math.tan(eps / 2) ** 2;
  const L0r = L0 * D2R;
  const eqTimeMin =
    4 *
    R2D *
    (y * Math.sin(2 * L0r) -
      2 * e * Math.sin(Mr) +
      4 * e * y * Math.sin(Mr) * Math.cos(2 * L0r) -
      0.5 * y * y * Math.sin(4 * L0r) -
      1.25 * e * e * Math.sin(2 * Mr));
  const minutesUtc = (((t % 86400000) + 86400000) % 86400000) / 60000;
  const trueSolarMin = minutesUtc + eqTimeMin + 4 * lon;
  const ha = angleDiffDeg(0, trueSolarMin / 4 - 180);
  const φ = lat * D2R;
  const har = ha * D2R;
  const cosZen = Math.sin(φ) * Math.sin(decl) + Math.cos(φ) * Math.cos(decl) * Math.cos(har);
  const zen = Math.acos(Math.max(-1, Math.min(1, cosZen)));
  const az = normalizeDeg(
    Math.atan2(Math.sin(har), Math.cos(har) * Math.sin(φ) - Math.tan(decl) * Math.cos(φ)) * R2D + 180,
  );
  return { elevationDeg: 90 - zen * R2D, azimuthDeg: az, hourAngleDeg: ha };
}

/** NOAA atmospheric refraction correction (degrees) for a geometric elevation. */
export function refractionDeg(elevDeg: number): number {
  if (elevDeg > 85) return 0;
  const te = Math.tan(elevDeg * D2R);
  let arcsec: number;
  if (elevDeg > 5) arcsec = 58.1 / te - 0.07 / te ** 3 + 0.000086 / te ** 5;
  else if (elevDeg > -0.575) arcsec = 1735 + elevDeg * (-518.2 + elevDeg * (103.4 + elevDeg * (-12.79 + elevDeg * 0.711)));
  else arcsec = -20.772 / te;
  return arcsec / 3600;
}

/** NOAA solar position algorithm. */
export function sunPosition(t: EpochMs, lat: number, lon: number): SunPosition {
  const g = solarGeometry(t, lat, lon);
  return { azimuthDeg: g.azimuthDeg, elevationDeg: g.elevationDeg + refractionDeg(g.elevationDeg) };
}

export interface SunTimes {
  /** Local civil day these times belong to, 'YYYY-MM-DD' in `timezone`. */
  date: string;
  astronomicalDawn: EpochMs | null;
  nauticalDawn: EpochMs | null;
  civilDawn: EpochMs | null; // −6°
  sunrise: EpochMs | null; // −0.833°
  /** Morning golden hour ends when the sun reaches +6°. */
  goldenHourMorningEnd: EpochMs | null;
  solarNoon: EpochMs;
  /** Evening golden hour starts when the sun descends through +6°. */
  goldenHourEveningStart: EpochMs | null;
  sunset: EpochMs | null;
  civilDusk: EpochMs | null;
  nauticalDusk: EpochMs | null;
  astronomicalDusk: EpochMs | null;
  /** Blue hour ≈ sun between −4° and −6°. */
  blueHourMorning: { start: EpochMs; end: EpochMs } | null;
  blueHourEvening: { start: EpochMs; end: EpochMs } | null;
  maxElevationDeg: number;
}

/** Apparent-elevation thresholds (degrees) used for photographic phases. */
export const GOLDEN_HOUR_UPPER_DEG = 6;
export const GOLDEN_HOUR_LOWER_DEG = -4;
export const BLUE_HOUR_LOWER_DEG = -6;
/** Geometric elevation of the sun's centre at sunrise/sunset. */
export const SUNRISE_ELEVATION_DEG = -0.833;

function transitNear(t: EpochMs, lat: number, lon: number): EpochMs {
  let n = t;
  for (let i = 0; i < 4; i++) {
    const ha = solarGeometry(n, lat, lon).hourAngleDeg;
    n -= (ha / 360) * 86400000;
  }
  return n;
}

type ElevFn = (t: EpochMs) => number;

/** Time in [a,b] where f crosses h (f monotone on the interval); null if no crossing. */
function findCrossing(f: ElevFn, h: number, a: EpochMs, b: EpochMs): EpochMs | null {
  let fa = f(a) - h;
  const fb = f(b) - h;
  if (fa === 0) return a;
  if (fb === 0) return b;
  if (fa * fb > 0) return null;
  let lo = a;
  let hi = b;
  // Bisection to ~50 ms then a final secant step.
  while (hi - lo > 50) {
    const mid = (lo + hi) / 2;
    const fm = f(mid) - h;
    if (fm === 0) return Math.round(mid);
    if (fa * fm < 0) hi = mid;
    else {
      lo = mid;
      fa = fm;
    }
  }
  return Math.round((lo + hi) / 2);
}

const sunTimesCache = new Map<string, SunTimes>();
const SUN_TIMES_CACHE_MAX = 64;

/** Sun event times for the local calendar day (in IANA `timezone`) containing `t`. */
export function sunTimes(t: EpochMs, lat: number, lon: number, timezone: string): SunTimes {
  const day = localDayBounds(t, timezone);
  const key = `${day.date}|${lat}|${lon}|${timezone}`;
  const hit = sunTimesCache.get(key);
  if (hit) return hit;

  const noon = transitNear((day.start + day.end) / 2, lat, lon);
  // Choose the transit that falls inside the local day (zones far from their meridian).
  let solarNoon = noon;
  if (solarNoon < day.start) solarNoon = transitNear(solarNoon + 86400000, lat, lon);
  else if (solarNoon >= day.end) solarNoon = transitNear(solarNoon - 86400000, lat, lon);

  const geo: ElevFn = (x) => solarGeometry(x, lat, lon).elevationDeg;
  const app: ElevFn = (x) => sunPosition(x, lat, lon).elevationDeg;
  const am0 = solarNoon - 43200000; // ≈ solar midnight before
  const pm1 = solarNoon + 43200000; // ≈ solar midnight after
  const rise = (f: ElevFn, h: number) => findCrossing(f, h, am0, solarNoon);
  const set = (f: ElevFn, h: number) => findCrossing(f, h, solarNoon, pm1);

  const civilDawn = rise(geo, -6);
  const civilDusk = set(geo, -6);
  const blueMorningEnd = rise(app, GOLDEN_HOUR_LOWER_DEG);
  const blueEveningStart = set(app, GOLDEN_HOUR_LOWER_DEG);

  const out: SunTimes = {
    date: day.date,
    astronomicalDawn: rise(geo, -18),
    nauticalDawn: rise(geo, -12),
    civilDawn,
    sunrise: rise(geo, SUNRISE_ELEVATION_DEG),
    goldenHourMorningEnd: rise(app, GOLDEN_HOUR_UPPER_DEG),
    solarNoon: Math.round(solarNoon),
    goldenHourEveningStart: set(app, GOLDEN_HOUR_UPPER_DEG),
    sunset: set(geo, SUNRISE_ELEVATION_DEG),
    civilDusk,
    nauticalDusk: set(geo, -12),
    astronomicalDusk: set(geo, -18),
    blueHourMorning: civilDawn != null && blueMorningEnd != null ? { start: civilDawn, end: blueMorningEnd } : null,
    blueHourEvening: blueEveningStart != null && civilDusk != null ? { start: blueEveningStart, end: civilDusk } : null,
    maxElevationDeg: app(solarNoon),
  };
  if (sunTimesCache.size >= SUN_TIMES_CACHE_MAX) {
    const first = sunTimesCache.keys().next().value;
    if (first !== undefined) sunTimesCache.delete(first);
  }
  sunTimesCache.set(key, out);
  return out;
}

/** Look up an event by name from SunTimes. */
export function sunEventTime(times: SunTimes, event: SunEventName): EpochMs | null {
  switch (event) {
    case 'sunrise': return times.sunrise;
    case 'sunset': return times.sunset;
    case 'golden_hour_morning_end': return times.goldenHourMorningEnd;
    case 'golden_hour_evening_start': return times.goldenHourEveningStart;
    case 'civil_dawn': return times.civilDawn;
    case 'civil_dusk': return times.civilDusk;
    default: return null;
  }
}

/** Display name for a sun event, e.g. "Sunset", "Golden hour". */
export function sunEventLabel(event: SunEventName): string {
  switch (event) {
    case 'sunrise': return 'Sunrise';
    case 'sunset': return 'Sunset';
    case 'golden_hour_morning_end': return 'Golden hour ends';
    case 'golden_hour_evening_start': return 'Golden hour';
    case 'civil_dawn': return 'Civil dawn';
    case 'civil_dusk': return 'Civil dusk';
    default: return String(event);
  }
}

/**
 * Phase from APPARENT elevation:
 *   < −18 night · −18..−12 astronomical twilight · −12..−6 nautical twilight ·
 *   −6..−4 blue hour · −4..+6 golden hour · ≥ +6 day.
 * Lower bounds are inclusive (e.g. exactly +6° is 'day', exactly −4° is 'golden_hour').
 */
export function lightPhase(elevationDeg: number): LightPhase {
  if (elevationDeg >= GOLDEN_HOUR_UPPER_DEG) return 'day';
  if (elevationDeg >= GOLDEN_HOUR_LOWER_DEG) return 'golden_hour';
  if (elevationDeg >= BLUE_HOUR_LOWER_DEG) return 'blue_hour';
  if (elevationDeg >= -12) return 'nautical_twilight';
  if (elevationDeg >= -18) return 'astronomical_twilight';
  return 'night';
}

export const LIGHT_PHASE_LABELS: Record<LightPhase, string> = {
  night: 'Night',
  astronomical_twilight: 'Astronomical twilight',
  nautical_twilight: 'Nautical twilight',
  blue_hour: 'Blue hour',
  golden_hour: 'Golden hour',
  day: 'Daylight',
};

export type Lighting = 'front-lit' | 'side-lit' | 'back-lit' | 'top-lit' | 'no-sun';

/** Flare risk when the sun is within this angle of the lens axis … */
export const FLARE_OFFSET_DEG = 25;
/** … and lower than this elevation. */
export const FLARE_MAX_ELEVATION_DEG = 25;
/** Above this elevation the light reads as top light regardless of direction. */
export const TOP_LIGHT_ELEVATION_DEG = 60;

/**
 * Cinematography read of the light on a subject seen from a camera looking along `cameraHeadingDeg`.
 * Sun behind the camera → subject front-lit; sun in front of camera → back-lit (flare risk).
 *
 * sunOffsetDeg = |azimuth difference| (0..180). Offset ≤ 60° → back-lit, ≥ 120° → front-lit,
 * otherwise side-lit. Elevation > 60° → top-lit; below −0.833° (sun set) → no-sun.
 */
export function lightingForView(cameraHeadingDeg: number, sun: SunPosition): {
  lighting: Lighting;
  /** Angle between camera look direction and sun azimuth, 0 = looking into sun. */
  sunOffsetDeg: number;
  flareRisk: boolean;
  summary: string; // e.g. "Back-lit — sun 12° off lens axis, flare risk"
} {
  const sunOffsetDeg = Math.abs(angleDiffDeg(cameraHeadingDeg, sun.azimuthDeg));
  const off = Math.round(sunOffsetDeg);
  if (sun.elevationDeg < SUNRISE_ELEVATION_DEG) {
    return { lighting: 'no-sun', sunOffsetDeg, flareRisk: false, summary: 'No direct sun — sun below the horizon' };
  }
  const flareRisk = sunOffsetDeg <= FLARE_OFFSET_DEG && sun.elevationDeg < FLARE_MAX_ELEVATION_DEG;
  let lighting: Lighting;
  if (sun.elevationDeg > TOP_LIGHT_ELEVATION_DEG) lighting = 'top-lit';
  else if (sunOffsetDeg <= 60) lighting = 'back-lit';
  else if (sunOffsetDeg >= 120) lighting = 'front-lit';
  else lighting = 'side-lit';

  const side = angleDiffDeg(cameraHeadingDeg, sun.azimuthDeg) >= 0 ? 'right' : 'left';
  let summary: string;
  switch (lighting) {
    case 'top-lit':
      summary = `Top-lit — sun ${Math.round(sun.elevationDeg)}° high, hard overhead light`;
      break;
    case 'back-lit':
      summary = `Back-lit — sun ${off}° off lens axis`;
      break;
    case 'front-lit':
      summary = `Front-lit — sun behind camera (${180 - off}° off)`;
      break;
    default:
      summary = `Side-lit — sun from camera ${side} (${off}° off lens axis)`;
  }
  if (flareRisk) summary += ', flare risk';
  return { lighting, sunOffsetDeg, flareRisk, summary };
}

function sunDirectionPhrase(pos: SunPosition): string {
  const e = Math.round(pos.elevationDeg);
  const az = Math.round(pos.azimuthDeg) % 360;
  const pt = compassPoint(pos.azimuthDeg);
  if (pos.elevationDeg < SUNRISE_ELEVATION_DEG) {
    return `sun below the horizon in the ${pt} (az ${az}°, ${MINUS}${Math.abs(e)}°)`;
  }
  const qual = pos.elevationDeg < 15 ? 'low in the' : pos.elevationDeg < 45 ? 'in the' : 'high in the';
  return `sun ${qual} ${pt} (az ${az}°, ${e}° up)`;
}

function compactDuration(sec: number): string {
  const min = Math.round(sec / 60);
  if (min < 1) return '<1 min';
  if (min < 60) return `${min} min`;
  return formatDuration(min * 60);
}

/** Human sentence for the current light, e.g. "Golden hour — sunset in 34 min, sun low in the WSW (az 251°, 4° up)". */
export function describeLight(t: EpochMs, lat: number, lon: number, timezone: string): string {
  const pos = sunPosition(t, lat, lon);
  const phase = lightPhase(pos.elevationDeg);
  const events: SunEventName[] = ['civil_dawn', 'sunrise', 'golden_hour_morning_end', 'golden_hour_evening_start', 'sunset', 'civil_dusk'];
  const phrases: Record<SunEventName, string> = {
    civil_dawn: 'civil dawn',
    sunrise: 'sunrise',
    golden_hour_morning_end: 'golden hour ends',
    golden_hour_evening_start: 'golden hour',
    sunset: 'sunset',
    civil_dusk: 'civil dusk',
  };
  let next: { name: SunEventName; at: EpochMs } | null = null;
  const today = sunTimes(t, lat, lon, timezone);
  const tomorrow = sunTimes(localDayBounds(t, timezone).end + 3600000, lat, lon, timezone);
  for (const st of [today, tomorrow]) {
    for (const ev of events) {
      const at = sunEventTime(st, ev);
      if (at != null && at > t && (!next || at < next.at)) next = { name: ev, at };
    }
    if (next) break;
  }
  const parts = [LIGHT_PHASE_LABELS[phase]];
  const tail: string[] = [];
  if (next) tail.push(`${phrases[next.name]} in ${compactDuration((next.at - t) / 1000)}`);
  tail.push(sunDirectionPhrase(pos));
  return `${parts[0]} — ${tail.join(', ')}`;
}
